import React, { useState } from "react";
import axios from "axios";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = { name: "", phone: "", email: "", guests: "", check_in: "", check_out: "", room_type: "", message: "" };

export default function InquiryForm() {
    const [form, setForm] = useState(initial);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error("Please enter your name and phone number.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/inquiry`, form);
            setDone(true);
            setForm(initial);
            toast.success("Inquiry received! We'll reach out shortly.");
        } catch (err) {
            console.error(err);
            toast.error("Couldn't send right now. Please try WhatsApp.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section className="relative py-24 md:py-32 bg-coffee-900 text-cream overflow-hidden" data-testid="inquiry-section">
            <div className="absolute inset-0 opacity-[0.08]" style={{
                backgroundImage: "radial-gradient(circle at 20% 20%, #E8B769 0%, transparent 40%), radial-gradient(circle at 80% 70%, #5F8747 0%, transparent 45%)"
            }} />

            <div className="relative max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                <div className="lg:col-span-5">
                    <p className="text-xs tracking-[0.3em] uppercase text-[#E8B769] mb-5">Plan your stay</p>
                    <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-tight">
                        Drop us a line, <span className="italic text-[#E8B769]">we'll call back.</span>
                    </h2>
                    <p className="mt-6 text-cream/75 text-lg leading-relaxed">
                        Tell us your dates and how big your group is — we'll send you a warm,
                        personalised plan (and the best rates) over WhatsApp.
                    </p>
                    <div className="mt-8 flex flex-col gap-2 text-cream/75 text-sm">
                        <span>📞 +91 9945888183 · +91 9741535246</span>
                        <span>✉️ sharath.veeras@gmail.com</span>
                        <span>📍 WQGM+79 Sakleshpura, Karnataka</span>
                    </div>
                </div>

                <form
                    onSubmit={submit}
                    className="lg:col-span-7 bg-cream text-coffee-900 p-8 md:p-10 rounded-2xl shadow-xl"
                    data-testid="inquiry-form"
                >
                    {done ? (
                        <div className="flex flex-col items-center text-center py-8" data-testid="inquiry-success">
                            <CheckCircle2 className="h-16 w-16 text-forest-700 mb-4" />
                            <h3 className="font-serif text-3xl mb-2">Thank you!</h3>
                            <p className="text-stone-600 max-w-sm">We've received your inquiry and will reach out to you very soon.</p>
                            <button
                                onClick={() => setDone(false)}
                                className="mt-6 text-sm underline text-coffee-700"
                                data-testid="inquiry-new-btn"
                            >
                                Send another inquiry
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <Field label="Your name *" name="name" value={form.name} onChange={onChange} testid="inquiry-name" />
                                <Field label="Phone *" name="phone" value={form.phone} onChange={onChange} testid="inquiry-phone" />
                                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} testid="inquiry-email" />
                                <Field label="Guests" name="guests" value={form.guests} onChange={onChange} testid="inquiry-guests" placeholder="e.g. 4 adults, 2 kids" />
                                <Field label="Check-in" name="check_in" type="date" value={form.check_in} onChange={onChange} testid="inquiry-checkin" />
                                <Field label="Check-out" name="check_out" type="date" value={form.check_out} onChange={onChange} testid="inquiry-checkout" />
                                <div className="md:col-span-2">
                                    <label className="text-xs tracking-[0.2em] uppercase text-coffee-600 mb-2 block">Room type</label>
                                    <select
                                        name="room_type"
                                        value={form.room_type}
                                        onChange={onChange}
                                        className="w-full bg-white border border-sand rounded-md px-4 py-3 text-base focus:outline-none focus:border-coffee-700"
                                        data-testid="inquiry-room-type"
                                    >
                                        <option value="">Select (optional)</option>
                                        <option>Family Retreat</option>
                                        <option>Couple Escape</option>
                                        <option>Private Cottage</option>
                                        <option>Event / Celebration</option>
                                    </select>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-xs tracking-[0.2em] uppercase text-coffee-600 mb-2 block">Tell us more</label>
                                    <textarea
                                        name="message"
                                        rows="3"
                                        value={form.message}
                                        onChange={onChange}
                                        placeholder="Any special requests?"
                                        className="w-full bg-white border border-sand rounded-md px-4 py-3 text-base focus:outline-none focus:border-coffee-700"
                                        data-testid="inquiry-message"
                                    />
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="mt-7 w-full md:w-auto inline-flex items-center justify-center gap-2 bg-coffee-900 text-cream px-8 py-4 rounded-full font-medium hover:bg-coffee-800 transition-colors disabled:opacity-60"
                                data-testid="inquiry-submit"
                            >
                                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                {loading ? "Sending..." : "Send inquiry"}
                            </button>
                        </>
                    )}
                </form>
            </div>
        </section>
    );
}

function Field({ label, name, type = "text", value, onChange, testid, placeholder }) {
    return (
        <div>
            <label className="text-xs tracking-[0.2em] uppercase text-coffee-600 mb-2 block">{label}</label>
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                className="w-full bg-white border border-sand rounded-md px-4 py-3 text-base focus:outline-none focus:border-coffee-700"
                data-testid={testid}
            />
        </div>
    );
}
